import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, getUser, logout } from '@/utils/api';
import { Ticket, Eye, Trash2, Settings, Calendar, Search, Download, ClipboardList, TrendingUp, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import AIInsightsPanel from './AIInsightsPanel';
import AdminNav from '@/components/AdminNav';
import DemoBanner from '@/components/DemoBanner';
import useDemoExpiry from '@/hooks/useDemoExpiry';

const STAGE_KEYS = [
  'replacement_prepared', 'replacement_dispatched', 'delivered_to_customer',
  'qc_done_at_customer_site', 'old_item_deinstalled', 'old_item_picked_up',
  'old_item_received_at_warehouse'
];

const QUICK_STATUS_OPTIONS = ['Awaiting', 'In Transit', 'At Customer', 'Pickup Pending', 'Completed', 'QC Check', 'Discard'];

const getReplacementStatus = (wo) => {
  if (!wo) return null;
  const stages = wo.stages || {};
  const doneCount = STAGE_KEYS.filter(k => stages[k]?.done).length;
  if (wo.status === 'Completed' || doneCount === 7) return { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' };
  if (doneCount === 0) return { label: 'Awaiting', color: 'bg-gray-100 text-gray-600 border-gray-200' };
  if (doneCount <= 2) return { label: 'In Transit', color: 'bg-blue-100 text-blue-700 border-blue-200' };
  if (doneCount <= 5) return { label: 'At Customer', color: 'bg-purple-100 text-purple-700 border-purple-200' };
  return { label: 'Pickup Pending', color: 'bg-amber-100 text-amber-700 border-amber-200' };
};

const METRICS_CONFIG = [
  { key: 'total_tickets', label: 'Total Tickets', emoji: '🎫', format: v => v, color: 'text-slate-800' },
  { key: 'closed_tickets', label: 'Resolved', emoji: '✅', format: v => v, color: 'text-slate-800' },
  { key: 'pending_tickets', label: 'Open / Pending', emoji: '⏳', format: v => v, color: 'text-slate-800' },
  { key: 'in_progress_tickets', label: 'In Progress', emoji: '🔁', format: v => v, color: 'text-blue-700' },
  { key: 'total_cost_incurred', label: 'Total Cost Incurred', emoji: '💰', format: v => `₹${(v||0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: 'text-green-700' },
  { key: 'avg_resolution_time_hours', label: 'Avg Resolution Time', emoji: '⏱', format: v => `${(v||0).toFixed(1)}h`, color: 'text-blue-700' },
  { key: 'overdue_tickets', label: 'Overdue Tickets', emoji: '⚠', format: v => v, color: 'text-red-600' },
  { key: 'sla_compliance_percentage', label: 'SLA Compliance', emoji: '📊', format: v => `${(v||0).toFixed(1)}%`, color: 'text-slate-800' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  useDemoExpiry();
  const isDemoMode = user?.role === 'demo';
  const [tickets, setTickets] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeTypeFilter, setActiveTypeFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [warrantyIssueTypeNames, setWarrantyIssueTypeNames] = useState(new Set());
  const [workOrdersMap, setWorkOrdersMap] = useState({});
  const [quickStatusDropdown, setQuickStatusDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [insightsSubTab, setInsightsSubTab] = useState('Quality Insights');
  const [fmeaAlertStatus, setFmeaAlertStatus] = useState(null);

  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);

  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'demo')) {
      navigate('/admin/login');
      return;
    }
    fetchData(dateFrom, dateTo);
    fetchFmeaAlertStatus();
  }, [dateFrom, dateTo]);

  const fetchFmeaAlertStatus = async () => {
    try {
      const res = await api.get('/insights/fmea/alert-status');
      setFmeaAlertStatus(res.data);
    } catch {
      // silently fail
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setQuickStatusDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!searchQuery) { setSearchResults(null); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const params = { q: searchQuery, from_date: dateFrom, to_date: dateTo };
        if (activeTypeFilter !== 'All') params.ticket_type = activeTypeFilter.toLowerCase();
        const res = await api.get('/tickets/search', { params });
        setSearchResults(res.data);
      } catch { toast.error('Search failed'); }
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery, activeTypeFilter, dateFrom, dateTo]);

  const fetchData = async (from, to) => {
    try {
      const params = { from_date: from, to_date: to };
      const [ticketsRes, metricsRes, issueTypesRes, workOrdersRes] = await Promise.all([
        api.get('/tickets', { params }),
        api.get('/dashboard/metrics', { params }),
        api.get('/issue-types'),
        api.get('/work-orders').catch(() => ({ data: [] }))
      ]);
      setTickets(ticketsRes.data);
      setMetrics(metricsRes.data);
      const types = issueTypesRes.data;
      setWarrantyIssueTypeNames(new Set(types.filter(t => t.is_warranty).map(t => t.name)));
      const woMap = {};
      (workOrdersRes.data || []).forEach(wo => {
        if (!woMap[wo.parent_ticket_id]) woMap[wo.parent_ticket_id] = [];
        woMap[wo.parent_ticket_id].push(wo);
      });
      setWorkOrdersMap(woMap);
    } catch {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const displayedTickets = useMemo(() => {
    const base = searchResults !== null ? searchResults : tickets;
    let filtered = base;
    if (searchResults === null) {
      if (activeTypeFilter === 'Warranty') filtered = filtered.filter(t => warrantyIssueTypeNames.has(t.issue_type) || t.is_warranty_ticket);
      else if (activeTypeFilter === 'Standard') filtered = filtered.filter(t => !warrantyIssueTypeNames.has(t.issue_type) && !t.is_warranty_ticket);
    }
    if (activeFilter !== 'All') filtered = filtered.filter(t => t.status === activeFilter);
    return filtered;
  }, [tickets, searchResults, activeTypeFilter, warrantyIssueTypeNames, activeFilter]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleExport = async () => {
    try {
      const params = { from_date: dateFrom, to_date: dateTo };
      if (activeTypeFilter !== 'All') params.ticket_type = activeTypeFilter.toLowerCase();
      const response = await api.get('/tickets/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'desko_export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch { toast.error('Export failed'); }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      fetchData(dateFrom, dateTo);
    } catch { toast.error('Failed to delete ticket'); }
  };

  const handleQuickStatus = async (woId, quickStatus) => {
    try {
      await api.patch(`/work-orders/${woId}/quick-status`, { quick_status: quickStatus });
      toast.success(`Work order updated: ${quickStatus}`);
      setQuickStatusDropdown(null);
      fetchData(dateFrom, dateTo);
    } catch { toast.error('Failed to update work order status'); }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'In-Progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'Closed': 'bg-green-100 text-green-800 border-green-200'
    };
    return <Badge className={variants[status] || 'bg-gray-100 text-gray-800'} data-testid={`status-${status.toLowerCase()}`}>{status}</Badge>;
  };

  const getTimeElapsed = (createdAt) => formatDistanceToNow(new Date(createdAt), { addSuffix: true });
  const isWarrantyTicket = (ticket) => warrantyIssueTypeNames.has(ticket.issue_type) || ticket.is_warranty_ticket ||
    ticket.issue_type?.toLowerCase().trim() === "register warranty";

  const getTypeFilteredForCounts = () => {
    if (activeTypeFilter === 'Warranty') return tickets.filter(t => isWarrantyTicket(t));
    if (activeTypeFilter === 'Standard') return tickets.filter(t => !isWarrantyTicket(t));
    return tickets;
  };

  const getTicketCountByStatus = (status) => {
    const base = getTypeFilteredForCounts();
    if (status === 'All') return base.length;
    return base.filter(t => t.status === status).length;
  };

  const navItems = [
    { label: 'Work Orders', icon: ClipboardList, onClick: () => navigate('/admin/work-orders'), testId: 'work-orders-nav-btn' },
    { label: 'Settings', icon: Settings, onClick: () => navigate('/admin/settings'), testId: 'settings-btn' },
  ];

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNav
        title="Admin Dashboard"
        titleIcon={Ticket}
        navItems={navItems}
        username={user?.first_name}
        onLogout={handleLogout}
      />
      <DemoBanner />

      {/* FMEA Critical Risk Banner */}
      {fmeaAlertStatus?.has_critical && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3" data-testid="fmea-critical-banner">
          <div className="container mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-medium text-red-800">
                Critical Risk Detected: {fmeaAlertStatus.critical_count} failure mode(s) with RPN &gt; 400.
              </span>
            </div>
            <button
              onClick={() => { setActiveTab('AI Insights'); setInsightsSubTab('FMEA Analysis'); }}
              className="text-sm font-semibold text-red-700 underline hover:text-red-900 whitespace-nowrap flex-shrink-0"
              data-testid="fmea-banner-link"
            >
              View FMEA Analysis →
            </button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 py-6 lg:py-8">
        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 mb-6 p-4 bg-card border border-border rounded-lg shadow-sm" data-testid="date-range-picker">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Date Range</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 text-sm" data-testid="date-from-input" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 text-sm" data-testid="date-to-input" />
            </div>
            <span className="text-xs text-muted-foreground hidden sm:block">Showing: {dateFrom} → {dateTo}</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto" data-testid="dashboard-tabs">
          {['Dashboard', 'AI Insights'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.toLowerCase().replace(' ', '-')}`}>
              {tab === 'AI Insights' && <TrendingUp className="h-4 w-4" />}
              {tab}
            </button>
          ))}
        </div>

        {/* AI Insights Tab */}
        {activeTab === 'AI Insights' && (
          <AIInsightsPanel
            dateFrom={dateFrom}
            dateTo={dateTo}
            initialSubTab={insightsSubTab}
            onSubTabChange={setInsightsSubTab}
          />
        )}

        {/* Dashboard Tab */}
        {activeTab === 'Dashboard' && (
        <>
          {/* Metrics — 2×4 on mobile, 4×2 on desktop */}
          {metrics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8" data-testid="metrics-grid">
                {METRICS_CONFIG.map(({ key, label, emoji, format, color }) => {
                  const value = metrics[key] ?? 0;
                  return (
                    <Card key={key} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`metric-${key}`}>
                      <CardContent className="p-3 sm:p-4">
                        <p className={`text-xl sm:text-2xl font-bold mb-1 ${color}`}>{format(value)}</p>
                        <p className="text-xs text-gray-500 font-medium">{emoji} {label}</p>
                        {key === 'overdue_tickets' && value > 0 && (
                          <p className="text-xs text-red-500 mt-1">Requires attention</p>
                        )}
                        {key === 'sla_compliance_percentage' && (
                          <div className="mt-1.5 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${value >= 80 ? 'bg-green-500' : value >= 60 ? 'bg-amber-400' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(value, 100)}%` }} />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {metrics.issue_type_summary && Object.keys(metrics.issue_type_summary).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6 sm:mb-8">
                  {Object.entries(metrics.issue_type_summary).map(([type, count]) => (
                    <div key={type} className="bg-white border border-gray-200 shadow-sm p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1 truncate">{type}</p>
                      <p className="text-xl font-bold text-slate-800">{count}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Tickets Section */}
          <div className="bg-white border border-border rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-border">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl sm:text-2xl font-heading font-bold">Tickets</h2>
                  {/* Export button visible on mobile only */}
                  <Button variant="outline" size="sm" onClick={handleExport} className="sm:hidden h-9" data-testid="export-csv-btn">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 flex-1">
                    <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <Input
                      placeholder="Search by ID, name, order, issue..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 text-base h-11"
                      data-testid="search-input"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExport} className="hidden sm:flex h-9" data-testid="export-csv-btn-desktop">
                    <Download className="h-4 w-4 mr-2" />Export CSV
                  </Button>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {/* Type filter */}
              <div className="mb-3 flex flex-wrap gap-2" data-testid="type-filter-tabs">
                {['All', 'Standard', 'Warranty'].map(type => (
                  <Button key={type} variant={activeTypeFilter === type ? 'default' : 'outline'} size="sm"
                    onClick={() => setActiveTypeFilter(type)} data-testid={`type-filter-${type.toLowerCase()}`}
                    className="h-9">
                    {type}
                  </Button>
                ))}
              </div>

              {/* Status filter */}
              <div className="mb-6 flex gap-2 flex-wrap" data-testid="filter-tabs">
                {['All', 'Pending', 'In-Progress', 'Closed'].map(status => (
                  <Button key={status} variant={activeFilter === status ? 'default' : 'outline'}
                    onClick={() => setActiveFilter(status)} data-testid={`filter-${status.toLowerCase()}`}
                    className="relative h-9">
                    {status}
                    <Badge variant="secondary" className="ml-2 rounded-full h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {getTicketCountByStatus(status)}
                    </Badge>
                  </Button>
                ))}
              </div>

              {searchResults !== null && (
                <p className="mb-3 text-sm text-muted-foreground" data-testid="search-result-count">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
                </p>
              )}

              {displayedTickets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">No tickets found</div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full" data-testid="tickets-table">
                      <thead className="border-b border-border">
                        <tr className="text-left">
                          <th className="pb-3 font-semibold text-sm">Ticket ID</th>
                          <th className="pb-3 font-semibold text-sm">Name</th>
                          <th className="pb-3 font-semibold text-sm">Order ID</th>
                          <th className="pb-3 font-semibold text-sm">Issue Type</th>
                          <th className="pb-3 font-semibold text-sm">Created / SLA</th>
                          <th className="pb-3 font-semibold text-sm">Status</th>
                          <th className="pb-3 font-semibold text-sm">WO Status</th>
                          <th className="pb-3 font-semibold text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedTickets.map((ticket) => {
                          const ticketWOs = workOrdersMap[ticket.ticket_id] || [];
                          return (
                            <tr key={ticket.ticket_id} className="border-b border-border last:border-0" data-testid="ticket-row">
                              <td className="py-4 text-sm font-mono">{ticket.ticket_id.substring(0, 8)}</td>
                              <td className="py-4 text-sm">{ticket.first_name}</td>
                              <td className="py-4 text-sm font-medium">{ticket.order_id}</td>
                              <td className="py-4 text-sm text-muted-foreground">{ticket.issue_type}</td>
                              <td className="py-4 text-sm text-muted-foreground">
                                {isWarrantyTicket(ticket) ? (
                                  <Badge className="bg-gray-100 text-gray-500 border border-gray-200" data-testid="warranty-no-sla-badge">
                                    Warranty — No SLA
                                  </Badge>
                                ) : (
                                  getTimeElapsed(ticket.created_at)
                                )}
                              </td>
                              <td className="py-4">{getStatusBadge(ticket.status)}</td>
                              <td className="py-4">
                                {ticketWOs.length > 0 ? (
                                  <div className="flex flex-col gap-1" ref={ticketWOs.some(wo => quickStatusDropdown?.woId === wo.work_order_id) ? dropdownRef : null}>
                                    {ticketWOs.map(wo => {
                                      const wStatus = getReplacementStatus(wo);
                                      const isOpen = quickStatusDropdown?.woId === wo.work_order_id;
                                      return (
                                        <div key={wo.work_order_id} className="relative">
                                          <button
                                            className={`text-xs px-2 py-0.5 rounded-full border cursor-pointer font-medium ${wStatus.color}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setQuickStatusDropdown(isOpen ? null : { woId: wo.work_order_id, ticketId: ticket.ticket_id });
                                            }}
                                            data-testid={`replacement-badge-${ticket.ticket_id}`}
                                          >
                                            {wo.replacement_type}: {wStatus.label} ▾
                                          </button>
                                          {isOpen && (
                                            <div className="absolute z-50 top-7 left-0 bg-white border border-border rounded-lg shadow-xl py-1 min-w-40"
                                              data-testid={`quick-status-dropdown-${ticket.ticket_id}`}>
                                              {QUICK_STATUS_OPTIONS.map(opt => (
                                                <button key={opt}
                                                  className="w-full text-left text-xs px-3 py-2 hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                  onClick={() => handleQuickStatus(wo.work_order_id, opt)}
                                                  disabled={isDemoMode}
                                                  title={isDemoMode ? 'Not available in demo' : undefined}
                                                  data-testid={`quick-status-option-${opt.toLowerCase().replace(/ /g, '-')}`}>
                                                  {opt}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="py-4">
                                <div className="flex gap-2">
                                  <Button variant="ghost" size="icon"
                                    onClick={() => navigate(`/admin/ticket/${ticket.ticket_id}`)}
                                    data-testid="view-ticket-btn">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon"
                                    onClick={() => handleDelete(ticket.ticket_id)}
                                    className="text-destructive hover:text-destructive"
                                    disabled={isDemoMode}
                                    title={isDemoMode ? 'Not available in demo' : undefined}
                                    data-testid="delete-ticket-btn">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden space-y-3" data-testid="tickets-mobile-list">
                    {displayedTickets.map((ticket) => {
                      const ticketWOs = workOrdersMap[ticket.ticket_id] || [];
                      return (
                        <div
                          key={ticket.ticket_id}
                          className="border border-border rounded-xl p-4 bg-white shadow-sm active:bg-muted/30 cursor-pointer"
                          onClick={() => navigate(`/admin/ticket/${ticket.ticket_id}`)}
                          data-testid="ticket-card-mobile"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-sm font-mono font-bold text-foreground">
                              #{ticket.ticket_id.substring(0, 8)}
                            </span>
                            {getStatusBadge(ticket.status)}
                          </div>
                          <p className="text-sm font-semibold text-foreground mb-0.5">{ticket.first_name}</p>
                          <p className="text-xs text-muted-foreground mb-2">{ticket.issue_type}</p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>Order: {ticket.order_id}</span>
                            {isWarrantyTicket(ticket) ? (
                              <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-xs">
                                Warranty
                              </Badge>
                            ) : (
                              <span>{getTimeElapsed(ticket.created_at)}</span>
                            )}
                          </div>
                          {ticketWOs.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {ticketWOs.map(wo => {
                                const wStatus = getReplacementStatus(wo);
                                return (
                                  <span key={wo.work_order_id} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${wStatus.color}`}>
                                    {wo.replacement_type}: {wStatus.label}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
